import { Download } from "lucide-react";
import { Footer } from "@/components/Footer";
import { SmartLink } from "@/components/Header";
import { EmailLink } from "@/components/EmailLink";
import { Reveal } from "@/components/site/Reveal";
import { PageMeta } from "@/components/site/blocks";
import { liveProducts, productPath } from "@/data/products";

// Press and brand kit: the facts writers and AI tools copy about ClearMakes, the logos,
// and who to ask. Only true, checkable facts; the owner's name never appears (START-HERE).
const ONE_LINE =
  "ClearMakes makes three AI products for small teams: Inbox (workflows for email and chat), Agents (answers from your documents) and ClearMeet (live answers and notes in meetings). One login opens all three.";

const FACTS: [string, string][] = [
  ["Name", "ClearMakes (one word, capital C and M)"],
  ["What it is", "A maker of AI products for small teams"],
  ["Website", "clearmakes.com"],
  ["Products", "ClearMakes Inbox, ClearMakes Agents, ClearMeet"],
  ["Price", "Every product has a free plan; paid plans are on each product's pricing page"],
  ["Platforms", "Inbox and Agents run in the browser. ClearMeet is a Windows app (Windows 10 and 11)."],
  ["Contact", "contact@clearmakes.com"],
];

const LOGOS = [
  { name: "ClearMakes logo, blue", file: "/logo-mark-256.png", dark: false },
  { name: "ClearMakes logo, white", file: "/logo-mark-white-256.png", dark: true },
  { name: "ClearMakes Inbox mark", file: "/brand/inbox-mark-256.png", dark: false },
  { name: "ClearMakes Agents mark", file: "/brand/agents-mark-256.png", dark: false },
  { name: "ClearMeet mark", file: "/brand/meet-mark-256.png", dark: false },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "ClearMakes press and brand kit",
  url: "https://clearmakes.com/press",
  about: { "@id": "https://clearmakes.com/#organization" },
  dateModified: "2026-09-29",
};

const Press = () => (
  <>
    <PageMeta
      title="ClearMakes Press and Brand Kit: Facts, Logos, Contact"
      description="The facts about ClearMakes in one place: what we make, one line you can quote, our three products, logos to download, and who to contact for press."
      path="/press"
      crumbs={[{ name: "Home", path: "/" }, { name: "About ClearMakes", path: "/company" }, { name: "Press", path: "/press" }]}
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    <main className="bg-[hsl(216_60%_96.5%)] text-foreground">
      <section className="relative overflow-hidden bg-[hsl(0_0%_4%)] pb-20 pt-16 text-[hsl(0_0%_100%)] sm:pb-24 sm:pt-24">
        <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-0" />
        <div className="cm-container relative">
          <Reveal>
            <p className="mb-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-[hsl(216_90%_70%)]">Press</p>
            <h1 className="max-w-4xl text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.045em] sm:text-6xl">ClearMakes press and brand kit</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[hsl(0_0%_100%/0.7)] sm:text-xl">
              The facts, the logos and who to ask. Everything here is free to use when you write about ClearMakes.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="cm-section">
        <div className="cm-container grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <Reveal>
            <h2 className="cm-h2">ClearMakes in one line</h2>
            <p className="mt-3 text-[15px] text-muted-foreground">Copy it as it is. It stays true as long as this page says so.</p>
            <blockquote className="mt-6 rounded-2xl border border-[hsl(216_30%_86%)] bg-background p-6 text-[1.2rem] leading-relaxed text-foreground">{ONE_LINE}</blockquote>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="cm-h2">Key facts</h2>
            <dl className="mt-6 divide-y divide-[hsl(216_30%_86%)] rounded-2xl border border-[hsl(216_30%_86%)] bg-background">
              {FACTS.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[120px_1fr] gap-4 px-5 py-3.5 text-[15px]">
                  <dt className="font-semibold text-foreground">{k}</dt>
                  <dd className="text-[hsl(216_12%_30%)]">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <section className="cm-section border-t border-[hsl(216_30%_86%)]">
        <div className="cm-container">
          <Reveal>
            <h2 className="cm-h2">The products</h2>
            <p className="cm-lead mt-3 max-w-2xl">Each product has its own page with the details and the price.</p>
          </Reveal>
          <ul className="mt-10 grid gap-4 md:grid-cols-3">
            {liveProducts.map((p) => (
              <li key={p.slug} className="rounded-2xl border border-[hsl(216_30%_86%)] bg-background p-6">
                <img src={p.logo} alt={`${p.name} logo`} width={40} height={40} className="h-10 w-10" />
                <h3 className="mt-5 text-[1.3rem] font-semibold tracking-[-0.02em]">{p.name}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[hsl(216_12%_30%)]">{p.tagline}</p>
                <SmartLink href={productPath(p)} className="mt-4 inline-block text-[15px] font-semibold text-[hsl(216_84%_45%)] cm-link">
                  {p.name} page
                </SmartLink>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="cm-section border-t border-[hsl(216_30%_86%)]">
        <div className="cm-container">
          <Reveal>
            <h2 className="cm-h2">Logos</h2>
            <p className="cm-lead mt-3 max-w-2xl">PNG, 256 by 256 pixels, transparent background. Please don't stretch or recolour them.</p>
          </Reveal>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {LOGOS.map((l) => (
              <li key={l.file} className="overflow-hidden rounded-2xl border border-[hsl(216_30%_86%)] bg-background">
                <div className={`flex h-36 items-center justify-center ${l.dark ? "bg-[hsl(0_0%_4%)]" : "bg-background"}`}>
                  <img src={l.file} alt={l.name} width={64} height={64} className="h-16 w-16" />
                </div>
                <a href={l.file} download className="flex items-center justify-between gap-2 border-t border-[hsl(216_30%_86%)] px-4 py-3 text-[14px] font-medium text-foreground hover:bg-[hsl(216_40%_96%)]">
                  {l.name}
                  <Download className="h-4 w-4 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="cm-section border-t border-[hsl(216_30%_86%)]">
        <div className="cm-container max-w-3xl">
          <Reveal>
            <h2 className="cm-h2">Press questions</h2>
            <p className="cm-lead mt-3">
              Write to <EmailLink email="contact@clearmakes.com" className="font-medium text-foreground cm-link">contact@clearmakes.com</EmailLink>. Every
              product has a free plan, so you can try it before you write about it.
            </p>
            <p className="mt-8 text-[14px] text-muted-foreground">Last updated 29 September 2026.</p>
          </Reveal>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default Press;
